import { inject, InjectionToken, NgModule } from '@angular/core';
import { CanActivateFn, NavigationError, Router, RouterModule, Routes, UrlTree } from '@angular/router';
import { LoginComponent } from './components/auth/login-page/login.component';
import { RegisterComponent, RegistrationGuard } from './components/auth/registration-page/register.component';
import { AddClientKeyContainerGuard, ClientKeyComponent } from './components/auth/client-key/client-key.component';
import { LogoutComponent } from './components/auth/logout/logout.component';
import { ResetPasswordComponent } from './components/auth/reset-password/reset-password.component';
import { PasswordResetEmailComponent } from './components/auth/check-email/password-reset/password-reset-email.component';
import { InvoicesComponent } from './components/main/invoices/invoices.component';
import { AccountSettingsComponent } from './components/main/account-settings/account-settings.component';
import { ReviewAndPayComponent } from './components/main/review-and-pay/review-and-pay.component';
import { MainComponent } from './components/main/main.component';
import { VerifyEmailComponent, VerifyEmailGuard } from './components/auth/check-email/verification/verify-email.component';
import { SettingsComponent } from './components/main/settings/settings.component';
import { PaymentsComponent } from './components/main/payments/payments.component';
import {
  ROUTE_TOKEN_CHECK_FOR_PASSWORD_RESET,
  ROUTE_TOKEN_ENGAGEMENTS_BASE,
  ROUTE_TOKEN_ERROR_PAGE,
  ROUTE_TOKEN_FILES,
  ROUTE_TOKEN_INVOICES,
  ROUTE_TOKEN_LOGIN,
  ROUTE_TOKEN_MAIN_BASE,
  ROUTE_TOKEN_NO_ACCESS,
  ROUTE_TOKEN_PAY_INVOICE,
  ROUTE_TOKEN_PAYMENTS,
  ROUTE_TOKEN_PROJECTS,
  ROUTE_TOKEN_QUICK_PAYMENT_LINK_ERROR,
  ROUTE_TOKEN_QUICK_PAYMENTS_UNAVAILABLE,
  ROUTE_TOKEN_QUICK_PAYMENTS,
  ROUTE_TOKEN_REQUEST_PASSWORD_RESET,
  ROUTE_TOKEN_RESET_PASSWORD,
  ROUTE_TOKEN_REVIEW_AND_PAY,
  ROUTE_TOKEN_CXP_TASKS,
  ROUTE_TOKEN_SECURE_LINK,
  ROUTE_TOKEN_SETTINGS,
  ROUTE_TOKEN_UNDER_MAINTENANCE,
  ROUTE_TOKEN_VERIFY_EMAIL,
} from './routing-constants';
import { EngagementHubContainerComponent, EngagementsContainerComponent } from '@aiwyn/engagements';
import { EngagementsNextEnabledGuard } from './features/engagements-next/guards/engagements-next-enabled.guard';
import {
  BrandingGuard,
  EngagementsGuard,
  GuestGuard,
  ROUTE_DATA_TOKEN_ENGAGEMENTS_IS_DETAIL_VIEW,
  UnderMaintenanceGuard,
  VerifyEncryptedEmailGuardService,
} from './guards';
import { UnderMaintenancePageComponent } from './components/auth/under-maintenance-page/under-maintenance-page.component';
import { LoginPasswordlessComponent } from './components/auth/login-passwordless/login-passwordless.component';
import { LoginGetHelpComponent } from './components/auth/login-get-help/login-get-help.component';
import { ResetPasswordWaitComponent } from './components/auth/reset-password-wait/reset-password-wait.component';
import { LoginPasswordlessWaitComponent } from './components/auth/login-passwordless-wait/login-passwordless-wait.component';
import { LoginPasswordlessExpiredComponent } from './components/auth/login-passwordless-expired/login-passwordless-expired.component';
import { OpenPortalComponent } from './components/open-portal/open-portal.component';
import {
  AdminLoginGuardService,
  AuthGuard,
  AuthService,
  CalculatedApplicationPermission,
  EncryptedContactIdRouteInterceptor,
  FeatureFlagGuard,
  FeatureFlagRouteData,
  FeaturePermission,
  GUARD_DATA_FEATURE_FLAG_FAILURE_ROUTE,
  GUARD_DATA_FEATURE_FLAG_NAMES,
  isContactUser,
  LoginRedirectionGuard,
  noAccessGuard,
  OpenPortalGuard,
  permissionGuard,
  PermissionGuardData,
  PostAuthRedirectGuard,
  PRE_AUTH_REDIRECT_ROUTE_INTERCEPTOR,
  ResetPasswordPageComponent,
  resetPasswordPageGuard,
  SecureLinkLoginGuardService,
  SecureLinkOutOfBoundsInterceptor,
  URL_PARAM_SECURE_LINK_KEY,
} from '@aiwyn/auth';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { ClientRequestsPageComponent } from '@aiwyn/client-requests';
import { LoadableStatus, Logger } from '@aiwyn/shared';
import { PaymentsState } from '@aiwyn/common/payments';
import {
  RegisterContactComponent,
  RegisterContactGuard,
} from './components/auth/login-components/register-contact/register-contact.component';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { PayInvoiceAuthGuard } from './auth/guards/pay-invoice-auth-guard.service';
import { provideStates, Store } from '@ngxs/store';
import { QuickPaymentLinkErrorPageComponent } from './components/open-portal';
import { CONFIG_GUARD_FIELDS, CONFIG_GUARD_REDIRECT_URL, ConfigGuard, ConfigServiceRouteData } from './auth/guards/config-guard.service';
import { GUARD_DATA_FEATURE_FLAG_INVERT } from './auth/guards/feature-flag-guard.service';
import { CXP_ROUTE_TOKEN_DELIVERABLE_DETAIL, CXP_ROUTE_TOKEN_REQUEST_DETAIL } from './features/cxp/models/routing-constants';
import { NoAccessPageComponent } from './components/auth/no-access-page/no-access-page.component';
import {
  ENGAGEMENTS_VISIBILITY_CHECK,
  FeatureVisibilityCheck,
  PAYMENTS_VISIBILITY_CHECK,
  PROJECTS_VISIBILITY_CHECK,
} from './auth/guards/feature-visibility-check.model';
import { CommonClientRequestsState } from './common/client-requests';

const PASSWORDLESS_LOGIN_FEATURE_FLAG_ROUTE_DATA: FeatureFlagRouteData = {
  [GUARD_DATA_FEATURE_FLAG_NAMES]: LaunchDarklyFlag.ENABLE_PASSWORDLESS_LOGIN,
  [GUARD_DATA_FEATURE_FLAG_FAILURE_ROUTE]: [ROUTE_TOKEN_LOGIN],
};

const OPEN_PORTAL_FEATURE_FLAG_ROUTE_DATA: FeatureFlagRouteData = {
  [GUARD_DATA_FEATURE_FLAG_NAMES]: LaunchDarklyFlag.ENABLE_OPEN_PAYMENT_PORTAL,
  [GUARD_DATA_FEATURE_FLAG_FAILURE_ROUTE]: [ROUTE_TOKEN_LOGIN],
};

const CLIENT_REQUESTS_CONFIG_ROUTE_DATA: ConfigServiceRouteData & PermissionGuardData = {
  [CONFIG_GUARD_FIELDS]: ['clientExperiencePortalEnabled'],
  [CONFIG_GUARD_REDIRECT_URL]: [ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_INVOICES],
  permissions: [FeaturePermission.PROJECTS_AND_CLIENT_REQUESTS],
};

const TASKS_VIEW_FF_ROUTE_DATA: FeatureFlagRouteData = {
  [GUARD_DATA_FEATURE_FLAG_NAMES]: LaunchDarklyFlag.CXP_ENABLE_TASKS_VIEW,
  [GUARD_DATA_FEATURE_FLAG_FAILURE_ROUTE]: [ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_PROJECTS],
};

// OLD MODULE DEPENDENCY: redirects /main/projects → /main/tasks when the tasks FF is ON.
// When the old /main/projects route is fully retired, remove this route data, the
// projectsFeatureFlagGuard, and the projects route entry below.
const PROJECTS_WITH_TASKS_FF_ROUTE_DATA: FeatureFlagRouteData = {
  [GUARD_DATA_FEATURE_FLAG_NAMES]: LaunchDarklyFlag.CXP_ENABLE_TASKS_VIEW,
  [GUARD_DATA_FEATURE_FLAG_INVERT]: true,
  [GUARD_DATA_FEATURE_FLAG_FAILURE_ROUTE]: [ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_CXP_TASKS],
};

/**
 * OLD MODULE DEPENDENCY: wraps FeatureFlagGuard for the projects route to allow access when a
 * `request` query param is present (deep link / external bookmark into a specific client request),
 * even if the tasks FF is ON. The CXP tasks flow no longer navigates here — it uses
 * /main/tasks/request/:requestId instead. This guard only serves legacy bookmarks and external links.
 * When the old /main/projects route is fully retired, remove this guard and its route data.
 */
const projectsFeatureFlagGuard: CanActivateFn = (route, state) => {
  const requestId = route.queryParams['request'];
  const deliverableId = route.queryParams['deliverable'];
  if (requestId || deliverableId) {
    const router = inject(Router);
    return inject(LaunchDarklyService)
      .getFeatureFlag(LaunchDarklyFlag.CXP_ENABLE_TASKS_VIEW)
      .pipe(
        take(1),
        map(tasksViewEnabled => {
          if (tasksViewEnabled) {
            if (requestId) {
              return router.createUrlTree([
                ROUTE_TOKEN_MAIN_BASE,
                ROUTE_TOKEN_CXP_TASKS,
                CXP_ROUTE_TOKEN_REQUEST_DETAIL,
                requestId,
              ]);
            }
            return router.createUrlTree([
              ROUTE_TOKEN_MAIN_BASE,
              ROUTE_TOKEN_CXP_TASKS,
              CXP_ROUTE_TOKEN_DELIVERABLE_DETAIL,
              deliverableId,
            ]);
          }
          return true;
        }),
      );
  }
  return inject(FeatureFlagGuard).canActivate(route, state);
};

const DMS_PERMISSION_GUARD_ROUTE_DATA: ConfigServiceRouteData & PermissionGuardData & FeatureFlagRouteData = {
  [GUARD_DATA_FEATURE_FLAG_NAMES]: LaunchDarklyFlag.DMS_FILE_TAB_ENABLED,
  [CONFIG_GUARD_FIELDS]: ['clientExperiencePortalEnabled'],
  [CONFIG_GUARD_REDIRECT_URL]: [ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_FILES],
  permissions: [FeaturePermission.PROJECTS_AND_CLIENT_REQUESTS],
};

const INVOICES_AND_PAYMENTS_PERMISSION_GUARD_ROUTE_DATA: ConfigServiceRouteData & PermissionGuardData = {
  [CONFIG_GUARD_FIELDS]: ['enableClientPortalPayments'],
  [CONFIG_GUARD_REDIRECT_URL]: [ROUTE_TOKEN_MAIN_BASE],
  permissions: [FeaturePermission.INVOICES_AND_PAYMENTS],
};

const ENGAGEMENTS_PERMISSION_GUARD_ROUTE_DATA: PermissionGuardData = {
  permissions: [FeaturePermission.ENGAGEMENTS],
};

const SETTINGS_PERMISSION_GUARD_ROUTE_DATA: PermissionGuardData = {
  applicationPermission: CalculatedApplicationPermission.SETTINGS,
};

const CLIENT_REQUESTS_GUARD = new InjectionToken<{ canActivate: CanActivateFn; }>('client-requests-guard');

const canActivateQplErrorPage: CanActivateFn = () => {
  const store = inject(Store);
  const linkFailedToLoad = store.selectSnapshot(PaymentsState.getQuickPaymentLinkIdStatus) === LoadableStatus.ERROR;
  // A dead link resolves successfully (status LOADED) but arrives flagged unusable,
  // so the status check alone would bounce that payer back to the open form.
  const linkLoadedDead = store.selectSnapshot(PaymentsState.getQuickPaymentLinkArgs)?.usable === false;
  return linkFailedToLoad || linkLoadedDead || inject(Router).createUrlTree([ROUTE_TOKEN_QUICK_PAYMENTS]);
};

/**
 * When updating Angular from 14 to 15, it was suggested to ensure all ActivatedRouteSnapshot objects have a title property.
 * However, our application does not use the title property in any ActivatedRouteSnapshot objects.
 * Therefore, we are not implementing this change.
 * If we start using the title property in the future, we will need to update these and other route objects.
 */
const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: ROUTE_TOKEN_LOGIN,
  },

  /**
   * Avoid out-of-bounds check for logout route
   */
  {
    path: 'logout',
    canActivate: [UnderMaintenanceGuard, BrandingGuard],
    component: LogoutComponent,
  },

  /**
   * ✨ Secure Link ✨ experience
   */
  {
    path: ROUTE_TOKEN_SECURE_LINK,
    canActivate: [EncryptedContactIdRouteInterceptor],
    children: [
      {
        path: '',
        loadChildren: () => import('@aiwyn/secure-link').then(module => module.SecureLinkModule)
      },
      {
        path: `:${URL_PARAM_SECURE_LINK_KEY}`,
        // Guard redirects 100% of the time
        canActivate: [SecureLinkLoginGuardService],
        loadChildren: () => import('@aiwyn/secure-link').then(module => module.SecureLinkModule)
      }
    ]
  },

  /**
   * Full Client Portal experience
   */
  {
    path: '',
    // Process the encrypted contactId that might be hiding in every route
    canActivate: [SecureLinkOutOfBoundsInterceptor, EncryptedContactIdRouteInterceptor],
    canMatch: [],
    children: [
      /**
       * Routes responsible for authentication
       */
      // AdminLogin route is responsible for logging in, but we cannot recycle existing tokens. Do not include in the login redirect routes.
      {
        // Admins access client portal from via firm portal 'Client' table.
        path: 'AdminLogin',
        canActivate: [AdminLoginGuardService, UnderMaintenanceGuard, BrandingGuard],
        // AdminLoginGuard will sign the user in and then redirect 100% of the time
        children: []
      },
      {
        //  Passwordless login is handled here via Google Identity Platform
        path: `${ROUTE_TOKEN_LOGIN}/passwordless`,
        pathMatch: 'full',
        canActivate: [FeatureFlagGuard],
        component: LoginPasswordlessComponent,
        data: PASSWORDLESS_LOGIN_FEATURE_FLAG_ROUTE_DATA,
      },
      // Where the actual resetting happens. Can no longer use Firebase's out-of-the-box page due to password complexity requirements.
      {
        path: ROUTE_TOKEN_RESET_PASSWORD,
        pathMatch: 'full',
        component: ResetPasswordPageComponent,
        canActivate: [resetPasswordPageGuard],
      },
      {
        path: '',
        canActivateChild: [LoginRedirectionGuard],
        children: [
          {
            // Client login is handled here via Google Identity Platform
            path: ROUTE_TOKEN_LOGIN,
            pathMatch: 'full',
            canActivate: [UnderMaintenanceGuard, BrandingGuard],
            component: LoginComponent,
          },
          {
            path: `${ROUTE_TOKEN_LOGIN}/passwordless/wait`,
            canActivate: [FeatureFlagGuard],
            component: LoginPasswordlessWaitComponent,
            data: PASSWORDLESS_LOGIN_FEATURE_FLAG_ROUTE_DATA,
          },
          {
            path: `${ROUTE_TOKEN_LOGIN}/passwordless/expired`,
            canActivate: [FeatureFlagGuard],
            component: LoginPasswordlessExpiredComponent,
            data: PASSWORDLESS_LOGIN_FEATURE_FLAG_ROUTE_DATA,
          },
          // {
          //   // Admins access client portal from via firm portal 'Client' table.
          //   path: 'AdminLogin',
          //   canActivate: [AdminLoginGuardService, UnderMaintenanceGuard, BrandingGuard],
          //   // AdminLoginGuard will sign the user in and then redirect 100% of the time
          //   children: []
          // },
          {
            path: `${ROUTE_TOKEN_LOGIN}/getHelp`,
            pathMatch: 'full',
            canActivate: [UnderMaintenanceGuard, BrandingGuard],
            component: LoginGetHelpComponent
          },
          {
            path: `${ROUTE_TOKEN_REQUEST_PASSWORD_RESET}/wait`,
            canActivate: [UnderMaintenanceGuard, BrandingGuard],
            component: ResetPasswordWaitComponent
          },
        ],
      },

      /**
       * Routes accessible without authentication
       */
      {
        // TODO: Motion to rename this page's route to 'sign-in-help' for clarity. Those in favor, say "aye".
        path: ROUTE_TOKEN_REQUEST_PASSWORD_RESET,
        canActivate: [UnderMaintenanceGuard, BrandingGuard],
        component: ResetPasswordComponent,
      },
      {
        path: ROUTE_TOKEN_CHECK_FOR_PASSWORD_RESET,
        canActivate: [UnderMaintenanceGuard, BrandingGuard],
        component: PasswordResetEmailComponent,
      },
      {
        path: 'checkEmailForAccountVerification',
        canActivate: [VerifyEmailGuard, UnderMaintenanceGuard, BrandingGuard],
        component: VerifyEmailComponent
      },
      {
        // If user clicks the "Create an account" link in the login page
        path: 'register',
        canActivate: [RegistrationGuard, UnderMaintenanceGuard, BrandingGuard],
        component: RegisterComponent,
      },
      {
        // If user clicks the URL in the "verify your email address" email that they received, it will lead them here
        path: ROUTE_TOKEN_VERIFY_EMAIL,
        canActivate: [VerifyEncryptedEmailGuardService, UnderMaintenanceGuard, BrandingGuard],
        children: [],
      },
      {
        path: 'registerContact',
        canActivate: [RegisterContactGuard, UnderMaintenanceGuard, BrandingGuard],
        component: RegisterContactComponent,
      },
      {
        // Client login is handled here via Google Identity Platform
        path: ROUTE_TOKEN_QUICK_PAYMENTS,
        canActivate: [FeatureFlagGuard, UnderMaintenanceGuard, BrandingGuard],
        data: OPEN_PORTAL_FEATURE_FLAG_ROUTE_DATA,
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [OpenPortalGuard],
            component: OpenPortalComponent,
          },
          {
            path: ROUTE_TOKEN_QUICK_PAYMENT_LINK_ERROR,
            canActivate: [canActivateQplErrorPage],
            component: QuickPaymentLinkErrorPageComponent,
          },
          {
            path: ROUTE_TOKEN_QUICK_PAYMENTS_UNAVAILABLE,
            component: QuickPaymentLinkErrorPageComponent,
            data: { portalOff: true },
          },
        ],
      },
      {
        /*
         * If user clicks the URL in the invoice email that they received, it will lead them here.
         * Admins can also access through firm portal 'Invoices' table.
         */
        path: ROUTE_TOKEN_PAY_INVOICE,
        canActivate: [PayInvoiceAuthGuard, UnderMaintenanceGuard, BrandingGuard],
        // component: PayInvoiceComponent,
        children: [],
      },
      {
        path: 'clientkey',
        canActivate: [AddClientKeyContainerGuard, UnderMaintenanceGuard, BrandingGuard],
        component: ClientKeyComponent,
      },
      {
        path: ROUTE_TOKEN_UNDER_MAINTENANCE,
        component: UnderMaintenancePageComponent,
        canActivate: [UnderMaintenanceGuard],
      },

      /**
       * Routes accessible after authentication
       */
      {
        path: ROUTE_TOKEN_MAIN_BASE,
        canActivate: [UnderMaintenanceGuard, BrandingGuard],
        canActivateChild: [AuthGuard, PRE_AUTH_REDIRECT_ROUTE_INTERCEPTOR],
        component: MainComponent,
        children: [
          {
            path: '',
            // First validate the user is allowed to navigate, then redirect post auth
            canActivate: [PostAuthRedirectGuard],
            children: [],
            pathMatch: 'full'
          },
          {
            path: ROUTE_TOKEN_PROJECTS,
            component: ClientRequestsPageComponent,
            canActivate: [BrandingGuard, ConfigGuard, CLIENT_REQUESTS_GUARD, projectsFeatureFlagGuard, permissionGuard],
            loadChildren: () => import('@aiwyn/client-requests').then(module => module.ClientRequestsModule),
            data: { ...CLIENT_REQUESTS_CONFIG_ROUTE_DATA, ...PROJECTS_WITH_TASKS_FF_ROUTE_DATA },
          },
          {
            path: ROUTE_TOKEN_FILES,
            loadComponent: () =>
              import(
                './features/dms/containers/files-page/files-page.component'
              ).then((module) => module.FilesPageComponent),
            providers: [provideStates([CommonClientRequestsState])],
            canActivate: [BrandingGuard, ConfigGuard, CLIENT_REQUESTS_GUARD, FeatureFlagGuard, permissionGuard],
            data: DMS_PERMISSION_GUARD_ROUTE_DATA,
          },
          {
            path: 'files',
            redirectTo: ROUTE_TOKEN_FILES,
            pathMatch: 'full'
          },
          {
            // payment portal takes optional param of client id and invoice num
            // URL must use the following format:
            // http://localhost:4200/client-portal/reviewAndPay?clientId=200&invoiceNum=cla50.cla51.cla52
            // where 200 is client id and cla50, cla51 and cla52 are invoice numbers
            // "." used as separator since it is an unreserved, valid URL character and does not
            // require encoding.
            path: ROUTE_TOKEN_REVIEW_AND_PAY,
            component: ReviewAndPayComponent,
            canActivate: [BrandingGuard, ConfigGuard, permissionGuard],
            data: INVOICES_AND_PAYMENTS_PERMISSION_GUARD_ROUTE_DATA,
          },
          {
            path: ROUTE_TOKEN_INVOICES,
            component: InvoicesComponent,
            canActivate: [BrandingGuard, ConfigGuard, permissionGuard],
            data: INVOICES_AND_PAYMENTS_PERMISSION_GUARD_ROUTE_DATA,
          },
          {
            path: ROUTE_TOKEN_PAYMENTS,
            component: PaymentsComponent,
            canActivate: [BrandingGuard, ConfigGuard, GuestGuard, permissionGuard],
            data: INVOICES_AND_PAYMENTS_PERMISSION_GUARD_ROUTE_DATA,
          },
          {
            path: ROUTE_TOKEN_SETTINGS,
            component: SettingsComponent,
            canActivate: [BrandingGuard, GuestGuard, permissionGuard],
            data: SETTINGS_PERMISSION_GUARD_ROUTE_DATA,
          },
          {
            path: 'accountSettings',
            component: AccountSettingsComponent,
            canActivate: [BrandingGuard, GuestGuard],
          },
          {
            path: 'deliverables/preview',
            loadChildren: () => import('./features/deliverables').then(module => module.DeliverablesModule),
            canActivate: [BrandingGuard],
          },

          // List stays legacy; only :engagementId swaps via the flag (next first, legacy fallback).
          {
            path: ROUTE_TOKEN_ENGAGEMENTS_BASE,
            canActivate: [BrandingGuard, permissionGuard],
            canActivateChild: [EngagementsGuard],
            data: ENGAGEMENTS_PERMISSION_GUARD_ROUTE_DATA,
            children: [
              {
                path: '',
                component: EngagementsContainerComponent,
                data: { [ROUTE_DATA_TOKEN_ENGAGEMENTS_IS_DETAIL_VIEW]: false }
              },
              {
                path: ':engagementId',
                canMatch: [EngagementsNextEnabledGuard],
                loadChildren: () => import('./features/engagements-next/engagements-next.routes').then(routes => routes.ENGAGEMENTS_NEXT_ROUTES),
              },
              {
                path: ':engagementId',
                component: EngagementHubContainerComponent,
                data: { [ROUTE_DATA_TOKEN_ENGAGEMENTS_IS_DETAIL_VIEW]: true }
              },
            ],
          },
          {
            path: ROUTE_TOKEN_CXP_TASKS,
            canActivate: [BrandingGuard, ConfigGuard, CLIENT_REQUESTS_GUARD, FeatureFlagGuard, permissionGuard],
            loadChildren: () => import('./features/cxp').then(module => module.CxpModule),
            data: { ...CLIENT_REQUESTS_CONFIG_ROUTE_DATA, ...TASKS_VIEW_FF_ROUTE_DATA },
          },
          {
            // App-level error page — every failing page redirects here rather than growing an
            // error route of its own, so the url is always /main/error/:statusCode.
            path: `${ROUTE_TOKEN_ERROR_PAGE}/:statusCode`,
            canActivate: [BrandingGuard],
            loadComponent: () => import('./features/error-page/containers/error-page-container/error-page-container.component')
              .then(container => container.ErrorPageContainerComponent),
          },
          {
            path: ROUTE_TOKEN_NO_ACCESS,
            canActivate: [noAccessGuard],
            component: NoAccessPageComponent,
          },
          /**
           * If user is attempting to reach unknown authenticated view, reroute them to invoices.
           *
           * Note: You may be asking yourself "why not 'projects'"? And that, my friend, can be answered by looking at this note.
           */
          {
            path: '**',
            redirectTo: ROUTE_TOKEN_INVOICES,
          }
        ]
      },
    ]
  },
  // Catch all for unknown routes
  {
    path: '**',
    redirectTo: ROUTE_TOKEN_LOGIN,
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { enableTracing: true }),
  ],
  exports: [
    RouterModule
  ],
  providers: [
    {
      provide: PROJECTS_VISIBILITY_CHECK,
      useFactory: (
        configService: ConfigService,
        authService: AuthService,
        launchDarklyService: LaunchDarklyService,
        logger: Logger,
      ) => (): Observable<boolean> => {
        if (!configService.clientExperiencePortalEnabled) {
          logger.info(
            'PROJECTS_VISIBILITY_CHECK :: Feature flag is disabled, user has no visibility to projects'
          );
          return of(false);
        }

        return combineLatest([authService.getUser(), launchDarklyService.getFeatureFlag(LaunchDarklyFlag.CXP_ENABLE_TASKS_VIEW)]).pipe(
          map(([user, tasksViewEnabled]) => {
            if (!isContactUser(user)) {
              return false;
            }
            return tasksViewEnabled ? true : user?.hasClientRequests();
          }),
          tap(doShowClientRequestsTab => {
            if (!doShowClientRequestsTab) {
              logger.info(
                'PROJECTS_VISIBILITY_CHECK :: User does not have client requests, cannot show projects feature'
              );
            }
          }),
        );
      },
      deps: [ConfigService, AuthService, LaunchDarklyService, Logger],
    },
    {
      provide: ENGAGEMENTS_VISIBILITY_CHECK,
      useFactory: (router: Router, engagementGuard: EngagementsGuard) => (): Observable<boolean> =>  {
        // Try to get navigation state from current navigation (during navigation lifecycle)
        // or from browser history state (after navigation completes)
        const navigation = router.getCurrentNavigation();
        const navigationState = navigation?.extras?.state ?? (typeof window !== 'undefined' ? window.history.state : undefined);

        const route = router.routerState.snapshot.root;
        const engagementId = route.params?.['engagementId'] && !isNaN(route.params?.['engagementId']) ? Number(route.params?.['engagementId']) : undefined;
        const forceRefresh = navigationState?.forceRefresh ?? false;

        return engagementGuard.isEngagementsVisible(engagementId, forceRefresh);
      },
      deps: [Router, EngagementsGuard],
    },
    {
      provide: PAYMENTS_VISIBILITY_CHECK,
      useFactory: (configService: ConfigService, authService: AuthService, logger: Logger) => (): Observable<boolean> => {
        if (!configService.enableClientPortalPayments) {
          logger.info(
            'PAYMENTS_VISIBILITY_CHECK :: Enabled Payment Config flag is disabled, user has no visibility to payments, invoices and settings'
          );
          return of(false);
        }

        return authService.getUser().pipe(
          map(user => user.hasPermission(FeaturePermission.INVOICES_AND_PAYMENTS))
        );
      },
      deps: [ConfigService, AuthService, Logger],
    },
    {
      provide: CLIENT_REQUESTS_GUARD,
      useFactory: (router: Router, projectVisibilityCheck: FeatureVisibilityCheck) => (): Observable<boolean | UrlTree> => {
        return projectVisibilityCheck().pipe(
          map(hasVisibility => hasVisibility ? true : router.createUrlTree([ROUTE_TOKEN_MAIN_BASE])),
        );
      },
      deps: [Router, PROJECTS_VISIBILITY_CHECK],
    },
  ]
})
export class AppRoutingModule {
  constructor(
    private readonly router: Router,
    private readonly logger: Logger,
  ) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationError),
      tap(navigationError => this.logger.debug('APP :: AppRoutingModule :: constructor :: Navigation Error', navigationError)),
    ).subscribe();
  }
}
